import type {
  ArrayField,
  SelectField,
  StructuredArrayField,
  StructuredArrayRowField,
  TextField,
} from "@/v2/framework/dialog/types";

import type { validateNonStructuredField } from "./dialog-leaf-validation";

type LeafValidator = typeof validateNonStructuredField;
type LeafField = TextField | SelectField | ArrayField;
type RowRecord = Record<string, unknown>;

const asRows = (value: unknown): RowRecord[] =>
  Array.isArray(value)
    ? value.map((row) => (row && typeof row === "object" ? (row as RowRecord) : {}))
    : [];

/** Default empty cell, or coerce a value from the API when `raw` is passed. */
const cellValue = (child: StructuredArrayRowField, raw?: unknown): unknown => {
  if (raw === undefined) {
    if (child.type === "array-field") return [];
    if (child.type === "select-field") return child.defaultValue ?? null;
    if (child.type === "text-field" && child.inputType === "number") return null;
    return "";
  }

  if (child.type === "array-field") return Array.isArray(raw) ? raw.slice() : [];
  if (child.type === "select-field") return raw != null ? raw : (child.defaultValue ?? null);
  if (child.type === "text-field" && child.inputType === "number") {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return raw == null ? "" : String(raw);
};

const rowFromItem = (item: unknown, children: StructuredArrayRowField[]): RowRecord => {
  const source = item && typeof item === "object" ? (item as RowRecord) : {};
  return Object.fromEntries(children.map((child) => [child.name, cellValue(child, source[child.name])]));
};

export const createEmptyStructuredRow = (children: StructuredArrayRowField[]): RowRecord =>
  Object.fromEntries(children.map((child) => [child.name, cellValue(child)]));

export const normalizeStructuredArrayValue = (
  raw: unknown,
  field: StructuredArrayField,
): RowRecord[] => {
  const rows = (Array.isArray(raw) ? raw : []).map((item) => rowFromItem(item, field.children));
  const padCount = Math.max(0, field.minItems - rows.length);
  const padded =
    padCount > 0
      ? rows.concat(Array.from({ length: padCount }, () => createEmptyStructuredRow(field.children)))
      : rows;

  return padded.length > field.maxItems ? padded.slice(0, field.maxItems) : padded;
};

export const structuredArrayBoundsError = (
  field: StructuredArrayField,
  value: unknown,
): string | undefined => {
  const length = Array.isArray(value) ? value.length : 0;
  if (length >= field.minItems && length <= field.maxItems) return undefined;
  return `${field.label}: must have between ${field.minItems} and ${field.maxItems} entries`;
};

const leafField = (child: StructuredArrayRowField, field: StructuredArrayField): LeafField =>
  Object.assign({}, child, { required: child.required ?? !!field.required });

const eachCellError = (
  field: StructuredArrayField,
  value: unknown,
  values: RowRecord,
  validateLeaf: LeafValidator,
  onError: (rowIndex: number, child: StructuredArrayRowField, message: string) => boolean | void,
): void => {
  for (const [rowIndex, row] of asRows(value).entries()) {
    for (const child of field.children) {
      const message = validateLeaf(leafField(child, field), row[child.name], values);
      if (message && onError(rowIndex, child, message)) return;
    }
  }
};

export const collectStructuredArrayErrors = (
  basePath: string,
  field: StructuredArrayField,
  value: unknown,
  values: RowRecord,
  validateLeaf: LeafValidator,
): Record<string, string | undefined> => {
  const out: Record<string, string | undefined> = {};

  const bounds = structuredArrayBoundsError(field, value);
  if (bounds) out[basePath] = bounds;

  eachCellError(field, value, values, validateLeaf, (rowIndex, child, message) => {
    out[`${basePath}.${rowIndex}.${child.name}`] = message;
  });

  const custom = field.validate?.(value, values);
  if (custom) out[basePath] = out[basePath] ? `${out[basePath]} · ${custom}` : custom;

  return out;
};

export const aggregateStructuredArrayMessage = (
  field: StructuredArrayField,
  value: unknown,
  values: RowRecord,
  validateLeaf: LeafValidator,
): string | undefined => {
  const bounds = structuredArrayBoundsError(field, value);
  if (bounds) return bounds;

  let first: string | undefined;
  eachCellError(field, value, values, validateLeaf, (rowIndex, _child, message) => {
    first = `${field.label}: row ${rowIndex + 1}: ${message}`;
    return true;
  });

  return first ?? field.validate?.(value, values);
};

export const replaceErrorsUnderStructuredBase = (
  prev: Record<string, string | undefined>,
  basePath: string,
  bucket: Record<string, string | undefined>,
): Record<string, string | undefined> => {
  const prefix = `${basePath}.`;
  const next = Object.fromEntries(
    Object.entries(prev).filter(([key]) => key !== basePath && !key.startsWith(prefix)),
  );
  return Object.assign(next, bucket);
};
